import useErrorHandling, { ErrorTypes } from "../../../errors/useErrorHandling";
import usePreferences from "./usePreferences";
import { SERVICENOW } from "../../../constants/serviceNames";
import useServiceNowUser from "./useServiceNowUser";
import integrationsClient from "../clients/integrationsClient";
import serviceNowMapper from "../mappers/serviceNowMapper";
import useSWRInfinite from "swr/infinite";
import { useMemo } from "react";
import { PROCURE_TO_PAY_INCIDENT_ROWS_PER_PAGE } from "../../../constants/generalConstants";
import { countAPIItems, reduceAPIItems } from "../../../utils/apiItemUtils";

const useServiceNowProcurementInvoiceCases = (viewClosed) => {
    const { servicesInMaintenanceMode, preferencesAreLoading } =
        usePreferences();
    const {
        serviceNowUser,
        serviceNowUserIsLoading,
        errorMessage: serviceNowUserErrorMessage,
    } = useServiceNowUser();
    const { throwAsyncError } = useErrorHandling();

    const getCacheKey = (pageCount) => {
        if (
            preferencesAreLoading ||
            serviceNowUserIsLoading ||
            !serviceNowUser
        ) {
            return null;
        }
        return ["procurement-invoice-cases", pageCount, viewClosed];
    };

    const {
        data: procurementInvoiceCasesData,
        size: loadedPageCount,
        setSize: setPagesToLoad,
        isLoading: procurementInvoiceCasesAreLoading,
        error: procurementInvoiceCasesError,
    } = useSWRInfinite(
        getCacheKey,
        async (args) => {
            try {
                const params = {
                    limit: PROCURE_TO_PAY_INCIDENT_ROWS_PER_PAGE,
                    offset: args[1] * PROCURE_TO_PAY_INCIDENT_ROWS_PER_PAGE,
                    show_closed: args[2],
                };
                if (servicesInMaintenanceMode && serviceNowUser) {
                    if (
                        !servicesInMaintenanceMode.some(
                            (service) => service === SERVICENOW
                        )
                    ) {
                        const pInvoiceCasesData =
                            await integrationsClient.getProcurementInvoiceCases(
                                params,
                                {
                                    "sn-user-data-token": serviceNowUser,
                                }
                            );
                        return serviceNowMapper.mapAPIProcurementInvoiceCasesToCaseRequests(
                            pInvoiceCasesData
                        );
                    }
                }
            } catch (error) {
                throwAsyncError(
                    ErrorTypes.ServiceNowIncidentsError,
                    "Unable to fetch incidents from Procure To Pay",
                    error
                );
                throw new Error(
                    "Unable to fetch incidents from Procure To Pay"
                );
            }
        },
        { fallbackData: [], parallel: true, revalidateFirstPage: false }
    );

    const procurementInvoiceCaseList = useMemo(() => {
        return reduceAPIItems(procurementInvoiceCasesData, []);
    }, [procurementInvoiceCasesData]);

    const procurementInvoiceCaseCount = useMemo(() => {
        return countAPIItems(procurementInvoiceCasesData);
    }, [procurementInvoiceCasesData]);

    return {
        procurementInvoiceCasesAreLoading,
        procurementInvoiceCaseCount,
        procurementInvoiceCaseList,
        loadedPageCount,
        setPagesToLoad,
        errorMessage:
            serviceNowUserErrorMessage || procurementInvoiceCasesError?.message,
    };
};

export default useServiceNowProcurementInvoiceCases;
